import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from './api';

const getStyles = (theme) => ({
  container: { 
    width: '8.5in', 
    minHeight: '11in', 
    padding: '1in', 
    margin: '20px auto', 
    fontFamily: 'serif', 
    backgroundColor: theme === 'dark' ? '#1e1e1e' : 'white', 
    color: theme === 'dark' ? '#e0e0e0' : '#212529',
    border: theme === 'dark' ? '1px solid #444' : '1px solid #ddd', 
    boxShadow: theme === 'dark' ? '0 0 5px rgba(255,255,255,0.1)' : '0 0 5px rgba(0,0,0,0.1)' 
  },
  header: { textAlign: 'center', marginBottom: '40px' },
  section: { marginBottom: '30px' },
  sectionTitle: { 
    fontSize: '18px', 
    borderBottom: theme === 'dark' ? '2px solid #666' : '2px solid #333', 
    paddingBottom: '5px', 
    marginBottom: '15px',
    color: theme === 'dark' ? '#e0e0e0' : '#212529'
  },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  item: { 
    borderBottom: theme === 'dark' ? '1px solid #444' : '1px solid #eee', 
    padding: '10px 0', 
    display: 'flex', 
    justifyContent: 'space-between',
    color: theme === 'dark' ? '#e0e0e0' : '#212529'
  },
  notes: { 
    fontSize: '14px', 
    color: theme === 'dark' ? '#aaa' : '#555', 
    marginLeft: '20px', 
    fontStyle: 'italic' 
  },
  printButton: { position: 'fixed', top: '20px', right: '20px', padding: '10px 15px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  buttonContainer: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    display: 'flex',
    gap: '10px',
    zIndex: 1000
  },
  button: {
    padding: '10px 15px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  navLink: {
    position: 'fixed',
    top: '20px',
    left: '20px',
    color: theme === 'dark' ? '#e0e0e0' : '#212529',
    textDecoration: 'none',
    fontSize: '16px',
    zIndex: 1000
  },
  '@media print': {
    body: { margin: 0, padding: 0 },
    printButton: { display: 'none' },
    container: { boxShadow: 'none', border: 'none' }
  }
});

function ReportPage({ theme = 'light' }) {
  const [inspection, setInspection] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [manHours, setManHours] = useState({});
  const { inspectionId } = useParams();
  const styles = getStyles(theme);

  useEffect(() => {
    if (inspectionId) {
      Promise.all([
        api.get(`/inspections/${inspectionId}/`),
        api.get(`/inspections/${inspectionId}/checklist/`),
        api.get(`/man-hours/?inspection=${inspectionId}`)
      ]).then(([inspectionRes, checklistRes, manHoursRes]) => {
        setInspection(inspectionRes.data);
        // Ensure we have an array for checklist items
        const checklistData = Array.isArray(checklistRes.data) ? checklistRes.data : [];
        setChecklistItems(checklistData);
        
        // Handle paginated response for man-hours
        let manHoursData = manHoursRes.data;
        if (manHoursRes.data.results) {
          manHoursData = manHoursRes.data.results;
        }
        
        const hoursMap = Array.isArray(manHoursData) ? manHoursData.reduce((acc, mh) => {
          acc[mh.card] = { id: mh.id, hours: mh.hours };
          return acc;
        }, {}) : {};
        setManHours(hoursMap);
      }).catch(err => {
        console.error("Error fetching report data:", err);
        setChecklistItems([]); // Set to empty array on error
        setManHours({});
      });
    }
  }, [inspectionId]);

  const handleDownloadPDF = () => {
    api.get(`/inspections/${inspectionId}/pdf-report/`, { responseType: 'blob' })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Inspection_Report_${inspection.buno}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(err => console.error("Error downloading PDF:", err));
  };

  if (!inspection) {
    return <div style={{ color: theme === 'dark' ? '#e0e0e0' : '#212529' }}>Loading Report...</div>;
  }

  
  const printStyles = `@media print { .no-print { display: none; } body { margin: 0; } .report-container { box-shadow: none; border: none; } }`;
  const totalManHours = Object.values(manHours).reduce((total, mh) => total + parseFloat(mh.hours || 0), 0);

      return (
        <div>
          <style>{printStyles}</style>
            <div style={styles.buttonContainer} className="no-print">
              <button onClick={() => window.print()} style={styles.button}>Print</button>
              <button onClick={handleDownloadPDF} style={{...styles.button, backgroundColor: '#007bff'}}>Download PDF</button>
            </div>
            <Link to="/" style={styles.navLink} className="no-print">&larr; Back to Home</Link>
            <div style={styles.container} className="report-container">
              <div style={styles.header}>
                <h1>Inspection Report</h1>
              </div>
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>Inspection Details</h2>
                    <div style={styles.detailGrid}>
                        <div><strong>BUNO:</strong> {inspection.buno}</div>
                        <div><strong>Site:</strong> {inspection.site}</div>
                        <div><strong>TMS:</strong> {inspection.tms}</div>
                        <div><strong>Start Date:</strong> {inspection.start_date ? new Date(inspection.start_date).toLocaleDateString() : 'N/A'}</div>
                        <div><strong>Phase:</strong> {inspection.checklist_type}</div>
                        <div><strong>Completion Date:</strong> {inspection.completion_date ? new Date(inspection.completion_date).toLocaleDateString() : 'N/A'}</div>
                        <div><strong>Work Bay:</strong> {inspection.work_bay}</div>
                        <div><strong>Man Hours:</strong> {totalManHours.toFixed(2)}</div>
                    </div>
                </div>
                <div style={styles.section}>
                  <h2 style={styles.sectionTitle}>Checklist Results</h2>
                  {(() => {
                    // Group checklist items by card
                    const groupedByCard = Array.isArray(checklistItems) ? checklistItems.reduce((acc, item) => {
                      const card = item.card || 'General';
                      if (!acc[card]) {
                        acc[card] = {
                          items: [],
                          cardManHours: manHours[card]
                        };
                      }
                      acc[card].items.push(item);
                      return acc;
                    }, {}) : {};

                    return Object.keys(groupedByCard).sort((a, b) => {
                      // Sort cards numerically (e.g., 1-1, 1-2, 2-1, etc.)
                      const parseCard = (cardStr) => {
                        if (!cardStr) return [0, 0];
                        const parts = String(cardStr).split('-');
                        const major = parseInt(parts[0], 10) || 0;
                        const minor = parseInt(parts[1], 10) || 0;
                        return [major, minor];
                      };
                      const [aMajor, aMinor] = parseCard(a);
                      const [bMajor, bMinor] = parseCard(b);
                      if (aMajor !== bMajor) return aMajor - bMajor;
                      return aMinor - bMinor;
                    }).map(card => (
                      <div key={card} style={{ marginBottom: '20px' }}>
                        <div style={{ 
                          ...styles.item, 
                          backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa', 
                          border: '2px solid #007bff',
                          marginBottom: '10px'
                        }}>
                          <div>
                            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>CARD: {card}</span>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                              Man Hours: {groupedByCard[card].cardManHours ? groupedByCard[card].cardManHours.hours : '0.00'}
                            </div>
                          </div>
                        </div>
                        {groupedByCard[card].items.sort((a, b) => (a.steps || '').localeCompare(b.steps || '')).map(item => (
                          <div key={item.id} style={{ ...styles.item, marginLeft: '20px', border: theme === 'dark' ? '1px solid #444' : '1px solid #dee2e6' }}>
                            <div>
                              <span>{item.work_area || 'N/A'} | {item.work_center || 'N/A'}</span>
                              {item.steps && <div style={styles.notes}>Step: {item.steps}</div>}
                              {item.notes && <div style={styles.notes}>Notes: {item.notes}</div>}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <strong>{item.status}</strong>
                              {item.completed_at && (
                                <div style={{ fontSize: '12px', color: theme === 'dark' ? '#888' : '#6c757d' }}>
                                  {new Date(item.completed_at).toLocaleString()}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                </div>

            {/* ADD TOTAL MAN HOURS */}
            <div style={{...styles.section, textAlign: 'right', fontSize: '18px', fontWeight: 'bold' }}>
                Total Man Hours: {totalManHours.toFixed(2)}
            </div>
          </div>
        </div>
    );
}

export default ReportPage;